/**
 * Native accounting types — replaces Xero-backed models.
 * Firestore is the single source of truth.
 */

// --- Invoice ---
export type InvoiceStatus = 'DRAFT' | 'APPROVED' | 'SENT' | 'PAID' | 'VOIDED';
export type LineAmountType = 'Exclusive' | 'Inclusive' | 'NoTax';

export interface LineItem {
  id?: string;
  description: string;
  quantity: number;
  unitAmount: number;
  taxType?: 'GST' | 'GST_FREE' | 'NONE';
  taxAmount?: number;
  lineAmount: number;
  accountCode?: string;
  itemCode?: string;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  reference: string | null;
  status: InvoiceStatus;
  currencyCode: string;
  total: number;
  amountDue: number;
  amountPaid: number;
  contactId: string | null;
  contactName: string | null;
  contactEmail?: string | null;
  sentToContact: boolean;
  date: string;           // ISO date YYYY-MM-DD or stored format
  dueDate: string;
  lineAmountTypes?: LineAmountType;
  lineItems?: LineItem[];
  legacy_xero_id?: string | null;
  createdAt: any;
  updatedAt: any;
}

// --- Bill (ACCPAY) ---
export type BillStatus = 'DRAFT' | 'APPROVED' | 'SENT' | 'PAID' | 'VOIDED';

export interface Bill {
  id: string;
  billNumber: string;
  reference: string | null;
  status: BillStatus;
  currencyCode: string;
  total: number;
  amountDue: number;
  amountPaid: number;
  contactId: string | null;
  contactName: string | null;
  contactEmail?: string | null;
  date: string;
  dueDate: string;
  lineAmountTypes?: LineAmountType;
  lineItems?: LineItem[];
  legacy_xero_id?: string | null;
  createdAt: any;
  updatedAt: any;
}

// --- Payment ---
export type PaymentMethod = 'BANK_TRANSFER' | 'CASH' | 'PAYPAL' | 'CARD' | 'OTHER';

export interface Payment {
  id: string;
  documentType: 'invoice' | 'bill';
  documentId: string;
  amount: number;
  date: string;
  method: PaymentMethod;
  reference: string | null;
  createdAt: any;
  createdBy?: string | null;
  legacy_xero_id?: string | null;
}

// --- Credit Note ---
export type CreditNoteStatus = 'DRAFT' | 'APPROVED' | 'PAID' | 'VOIDED';

export interface CreditNoteAllocation {
  invoiceId: string;
  amount: number;
}

export interface CreditNote {
  id: string;
  creditNoteNumber: string;
  status: CreditNoteStatus;
  contactId: string | null;
  contactName: string | null;
  total: number;
  remainingCredit: number;
  date: string;
  lineItems?: LineItem[];
  allocations?: CreditNoteAllocation[];
  legacy_xero_id?: string | null;
  createdAt: any;
  updatedAt: any;
}

// --- Items (line item presets) ---
export interface Item {
  id: string;
  code: string;
  name: string;
  description: string;
  unitAmount: number;
  accountCode: string | null;
  taxType: 'GST' | 'GST_FREE' | 'NONE';
  isSold: boolean;
  isPurchased: boolean;
  createdAt: any;
  updatedAt: any;
  legacy_xero_id?: string | null;
}

// --- Tax ---
export interface TaxRate {
  id: string;
  name: string;
  rate: number;       // e.g. 10 for GST
  type: 'GST' | 'GST_FREE' | 'NONE';
}

// --- App / Business settings (for PDF, email) ---
export interface BusinessDetails {
  businessName: string;
  abn: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  logoUrl: string | null;
  paymentTerms: string | null;
  bankDetails: string | null;
  updatedAt: any;
}

// --- Repeating Invoice ---
export type RepeatingFrequency = 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';

export type RepeatingTemplateType = 'invoice' | 'bill';

export interface RepeatingInvoice {
  id: string;
  templateType?: RepeatingTemplateType;
  contactId: string | null;
  contactName: string | null;
  frequency: RepeatingFrequency;
  startDate: string;
  endDate: string | null;
  nextDate: string;
  scheduleAnchorDate?: string;
  lineItems: LineItem[];
  lineAmountTypes?: LineAmountType;
  /** Template total when stored by backend; otherwise derived from lineItems */
  total?: number;
  reference: string | null;
  status: 'ACTIVE' | 'PAUSED' | 'CANCELLED';
  /** Invoices only: auto-draft the client email (with PDF) when this template
   *  generates. Default ON (absent = enabled); the row toggle sets it false. */
  autoEmailDraft?: boolean;
  createdAt: any;
  updatedAt: any;
  legacy_xero_id?: string | null;
}

// --- Counters (Firestore docs for auto-increment) ---
export interface InvoiceCounter {
  lastInvoiceNumber: number;
  lastBillNumber: number;
  updatedAt: any;
}

// --- Filters (for lists) ---
export interface InvoiceFilters {
  status?: InvoiceStatus | InvoiceStatus[];
  contactId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface BillFilters {
  status?: BillStatus | BillStatus[];
  contactId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

// UI status config for badges
export interface InvoiceStatusConfig {
  label: string;
  badgeClass: string;
  icon?: string;
}

export const INVOICE_STATUS_CONFIG: Record<InvoiceStatus, InvoiceStatusConfig> = {
  DRAFT: { label: 'Draft', badgeClass: 'badge-light-secondary', icon: 'bi-file-earmark' },
  APPROVED: { label: 'Awaiting Payment', badgeClass: 'badge-light-warning', icon: 'bi-clock' },
  SENT: { label: 'Awaiting Payment', badgeClass: 'badge-light-warning', icon: 'bi-clock' },
  PAID: { label: 'Paid', badgeClass: 'badge-light-success', icon: 'bi-check-circle' },
  VOIDED: { label: 'Voided', badgeClass: 'badge-light-danger', icon: 'bi-x-circle' },
};

export const BILL_STATUS_CONFIG = INVOICE_STATUS_CONFIG as Record<BillStatus, InvoiceStatusConfig>;
